// Marker + line styling tokens for the gun barrel's selection/highlight state.

import { escape as escapeHtml } from 'lodash-es';

// Selected-well marker — thick black ring stays legible over any fill color.
export const SELECTED_MARKER_RADIUS = 11;
export const SELECTED_MARKER_LINE_WIDTH = 4;
export const SELECTED_MARKER_LINE_COLOR = '#000000';

// Vertical dots are scaled down: a vertical is a full-height stick plus its dot,
// so at the shared size the dot dominated. A SCALE (not a fixed radius) keeps the
// size hierarchy (selected > subject > offset) and leaves horizontals untouched.
export const VERTICAL_MARKER_RADIUS_SCALE = 0.5;

// Opacity applied to non-selected wells (and the vertical-spans line) when a
// selection is active so the highlight reads forward.
export const DIMMED_WELL_ALPHA = 0.25;
export const DIMMED_VERTICAL_LINE_OPACITY = 0.25;

// Pre-drill (permit) markers: semi-transparent pink (#ff69b4, matching the 3D
// viewer + map) with a dashed ring applied via CSS class. The 2D Profile's
// permit line uses the same pink but near-opaque (PRE_DRILL_LINE_ALPHA in
// utils.ts) so it stays readable over the formation bands.
export const PERMIT_MARKER_COLOR = '#ff69b4';
export const PERMIT_FILL_ALPHA = 0.5;

// Estimated (synthesized vertical) geometry: same alpha the 3D viewer uses for
// its dashed + semi-transparent estimated-bore line (well-3d-viewer.tsx), so
// the "lower-confidence estimate" treatment reads consistently across the 3D
// viewer, gun barrel, and 2D Profile.
export const ESTIMATED_ALPHA = 0.55;

// `#rrggbb` / `#rrggbbaa` → `rgba(...)`. Non-hex inputs pass through unchanged.
export function withAlpha(color: string, alpha: number): string {
  const hex = color.trim();
  if (!hex.startsWith('#') || (hex.length !== 7 && hex.length !== 9)) {
    return color;
  }
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return color;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Well data-label text: the escaped name (highlighted wells fall back to the
 * UWI tail), wrapped in a faded span when the well is dimmed.
 *
 * @param {object} opts - The well point's options.
 * @return {string | null} The label, or null when the well has nothing to show.
 */
export function wellDataLabel(opts: {
  name?: string;
  uwi14?: string;
  isDimmed?: boolean;
  isSelected?: boolean;
}): string | null {
  const label =
    opts.name || (opts.isSelected && opts.uwi14 ? opts.uwi14.slice(-4) : null);
  if (!label) return null;
  const safeLabel = escapeHtml(label);
  if (!opts.isDimmed) return safeLabel;
  return `<span style="opacity:${DIMMED_WELL_ALPHA}">${safeLabel}</span>`;
}
